import { useLanguage } from "@/i18n/LanguageContext";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { useGlossary } from "@/hooks/useCatalog";
import { ChevronRight } from "lucide-react";

export const GlossaryIndex = () => {
  const { t } = useLanguage();
  const { glossary } = useGlossary();
  return (
  <div className="min-h-screen">
    <SEO
      title={t("Manufacturing & MES Glossary")}
      description={t("Clear definitions of OEE, MES, ERP, EnMS, QMS, CMMS, SIRI, Andon, Kaizen, SPC and other manufacturing digitalization terms used across the BEEDIGIT platform.")}
    />
    <Navigation />
    <main className="pt-24 pb-24">
      <section className="hero-wash">
        <div className="container mx-auto px-4 py-16 max-w-4xl">
          <p className="eyebrow mb-3">{t("Resources")}</p>
          <h1 className="text-4xl md:text-5xl font-extrabold leading-tight mb-4">
            {t("Manufacturing & MES Glossary")}
          </h1>
          <p className="text-lg text-muted-foreground mb-10">
            {t("Plain-language definitions for the terms used in manufacturing digitalization, MES, OEE, energy management, quality and continuous improvement — as applied inside the BEEDIGIT platform.")}
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            {glossary.map((g) => (
              <Link
                key={g.slug}
                to={`/glossary/${g.slug}`}
                className="block rounded-lg border border-border p-5 hover:border-primary transition-colors"
              >
                <div className="text-sm font-semibold text-foreground mb-1">{t(g.term)}</div>
                <div className="text-sm text-muted-foreground">{t(g.short)}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
    <Footer />
  </div>
  );
};

export const GlossaryTerm = () => {
  const { t } = useLanguage();
  const { getEntry: getGlossaryEntry, ready } = useGlossary();
  const { slug = "" } = useParams();
  const entry = getGlossaryEntry(slug);
  const { pathname } = useLocation();

  // Aliases (e.g. /glossary/lot) resolve to the canonical term URL (/glossary/batch).
  if (entry && entry.slug !== slug) {
    return <Navigate to={pathname.replace(/\/glossary\/[^/]+$/, `/glossary/${entry.slug}`)} replace />;
  }

  if (!entry && !ready) return <div className="min-h-screen" />;
  if (!entry) {
    return (
      <div className="min-h-screen">
        <SEO title={t("Glossary term not found")} description={t("This glossary entry does not exist.")} />
        <Navigation />
        <main className="pt-24 pb-24">
          <div className="container mx-auto px-4 py-16 max-w-3xl">
            <h1 className="text-3xl font-bold mb-3">{t("Term not found")}</h1>
            <p className="text-muted-foreground mb-6">
              {t("We couldn't find that glossary entry. Browse all terms in the glossary index.")}
            </p>
            <Link to="/glossary" className="text-primary underline">
              {t("Back to glossary")}
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <SEO
        title={`${t(entry.term)} — ${t("Glossary")}`}
        description={t(entry.short)}
      />
      <Navigation />
      <main className="pt-24 pb-24">
        <section className="hero-wash">
          <div className="container mx-auto px-4 py-16 max-w-3xl">
            <nav className="flex items-center gap-1 text-xs text-muted-foreground mb-4">
              <Link to="/" className="hover:text-foreground">{t("Home")}</Link>
              <ChevronRight className="h-3 w-3" />
              <Link to="/glossary" className="hover:text-foreground">{t("Glossary")}</Link>
            </nav>
            <p className="eyebrow mb-3">{t("Glossary term")}</p>
            <h1 className="text-3xl md:text-4xl font-extrabold leading-tight mb-4">{t(entry.term)}</h1>
            <p className="text-lg text-muted-foreground mb-6">{t(entry.short)}</p>
            <div className="prose prose-neutral max-w-none prose-headings:text-foreground prose-p:text-foreground/85 prose-li:text-foreground/85">
              <p>{t(entry.definition)}</p>
            </div>
            {entry.relatedTerms && entry.relatedTerms.length > 0 && (
              <div className="mt-10">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">
                  {t("Related terms")}
                </div>
                <div className="flex flex-wrap gap-2">
                  {entry.relatedTerms.map((slug) => {
                    const r = getGlossaryEntry(slug);
                    if (!r || r.slug === entry.slug) return null;
                    return (
                      <Link
                        key={slug}
                        to={`/glossary/${r.slug}`}
                        className="text-xs px-3 py-1.5 rounded-full border border-border hover:border-primary"
                      >
                        {t(r.term)}
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};